import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Template } from '@repo/shared/domain';
import { EventEmitter } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { getTemplate, updateTemplate, resetTemplate } from 'src/api/admin/system';
import EmailTemplateCodeDialog from 'src/components/CodeDialog/EmailTemplateCodeDialog';

export const Route = createFileRoute('/_admin/system/templates/$id')({
	loader: async ({ params }) => {
		const template = await getTemplate(params.id);
		return { template };
	},
	component: TemplateDetailPage,
});

function TemplateDetailPage() {
	const { id } = Route.useParams();
	const { template: initialTemplate } = Route.useLoaderData();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { data: template, refetch: refetchTemplate } = useQuery({
		queryKey: ['template-detail', id],
		queryFn: () => getTemplate(id),
		initialData: initialTemplate,
	});

	useBreadcrumbLevel(0, 'System');
	useBreadcrumbLevel(1, 'Templates', '/system/templates');
	useBreadcrumbLevel(2, template?.id || 'Template Details');

	const handleSave = async (data: string) => {
		try {
			await updateTemplate(template.id, data);
			refetchTemplate();
			queryClient.invalidateQueries({ queryKey: ['templates'] });
		} catch (_err) {
			// ignore
		}
	};

	const handleReset = async () => {
		try {
			await resetTemplate(template.id);
			refetchTemplate();
			queryClient.invalidateQueries({ queryKey: ['templates'] });
		} catch (_err) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Error resetting template',
			});
		}
	};

	const handleClose = () => {
		navigate({ to: '/system/templates' });
	};

	return (
		<EmailTemplateCodeDialog
			isOpen={true}
			emailTemplate={template}
			onSave={handleSave}
			onClose={handleClose}
			onReset={handleReset}
		/>
	);
}
